import { apiFetch } from "./client";

const BASE = "/professionnel/statistiques";

export type Period = "30j" | "60j" | "90j" | "6m" | "9m" | "12m" | "1a";

export interface MonActiviteData {
  period: string;
  consultations: {
    total: number;
    surPeriode: number;
    parStatut: { ouverte: number; fermee: number; annulee: number };
    parType: Record<string, number>;
    evolutionMensuelle: Array<{ mois: string; total: number }>;
  };
  ordonnances: { total: number };
  analyses: { total: number };
  patientsDistincts: number;
}

export interface MesEtatsSortieData {
  period: string;
  totalFermees: number;
  parEtat: {
    gueri: number;
    en_traitement: number;
    sorti_sans_avis_medical: number;
    decede: number;
    sans_etat: number;
  };
  taux: { guerison: number; sortiSansAvis: number; deces: number };
  consultationsNonCloturees: number;
}

export interface MonTableauBordData {
  statistiquesParPeriode: Array<{
    periode: string;
    dateGeneration: string;
    plateforme: {
      /** Scopés à ce professionnel (pas la plateforme entière malgré le nom). */
      totalInscrits: number;
      nouveauxInscrits: number;
      /** Toujours 0 — pas de source SQL fiable par médecin, ne pas afficher. */
      dossiersCrees: number;
      dossiersModifies: number;
      consultationsRealisees: number;
      analysesPrescrites: number;
      ordonnancesDelivrees: number;
    };
    santeGlobale: { parfaiteSante: number; enTraitement: number; deces: number; total: number };
  }>;
  assurances: {
    total: number;
    parType: { AMO: number; prive: number; mutuelle: number; autre: number };
    parStatut: { valide: number; en_attente: number; rejetee: number; expiree: number };
  };
}

/** Le backend enveloppe toujours la charge utile dans `{ success, message, data, meta }`. */
function extractPayload<T>(raw: unknown): T {
  if (raw && typeof raw === "object" && !Array.isArray(raw) && "data" in raw) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

export async function fetchMonActivite(
  period: Period
): Promise<{ ok: true; data: MonActiviteData } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(`${BASE}/mon-activite?period=${period}`);
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: extractPayload<MonActiviteData>(out.data) };
}

export async function fetchMesEtatsSortie(
  period: Period
): Promise<{ ok: true; data: MesEtatsSortieData } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(`${BASE}/mes-etats-sortie?period=${period}`);
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: extractPayload<MesEtatsSortieData>(out.data) };
}

export async function fetchMonTableauBord(): Promise<
  { ok: true; data: MonTableauBordData } | { ok: false; message: string }
> {
  const out = await apiFetch<unknown>(`${BASE}/mon-tableau-bord`);
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: extractPayload<MonTableauBordData>(out.data) };
}
